import { Box, Paper, Skeleton, Stack, TablePaginationProps } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { DataGridProProps, GridColDef, GridSortModel, DataGridPro as MuiDataGrid } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { HeaderCell } from '../components/Cells/HeaderCell';
import { ComponentLoader } from '../components/ComponentLoader';
import { FilterPanel } from '../components/FilterPanel/FilterPanel';
import { Combiner } from '../components/FilterPanel/components/Combiner';
import { FilterMapper } from '../components/FilterPanel/mappers/FilterMapper';
import { QueryFilterMapper } from '../components/FilterPanel/mappers/QueryFilterMapper';
import { FilterConfiguration } from '../components/FilterPanel/types';
import { getDefaultCombiner } from '../components/FilterPanel/utils';
import { MemoizedMuiDataGrid } from '../components/MemoizedMuiDataGrid';
import { TableHeader } from '../components/TableHeader';
import { QueryParamManager } from '../managers/QueryParamManager';
import { SorterMapper } from '../mappers/SorterMapper';
import { dataGridStyleOverrides } from '../style/data-grid-styles';
import { FilterOperator, SorterSet as GQLSorterSet } from '../types/gql-types';
import { BaseColumnModel, DefaultHeaderConfiguration, FieldConfiguration } from '../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, DEFAULT_PAGE, DEFAULT_PAGE_INFO, PageInfo, PaginationResult } from '../types/pagination';
import { bind } from '../utils/decorators';
import { BaseController, BaseState } from './BaseController';

export type PaginatedTableState = BaseState & {
  /**
   * Indicates the loading state while we fetch data.
   */
  loadingData: boolean;
  /**
   * Indicates if we have done the initial load.
   * Can be used to differentiate between skeleton loading and normal data loading.
   */
  initialDataLoad: boolean;
  /**
   * If any load returns some results, we can use this to display a different blank state
   * from the table blank state.
   */
  hadAnyData: boolean;
  /**
   * Pagination info about cursors.
   */
  pageInfo: PageInfo;
  /**
   * Total number of items to show in table.
   */
  totalCount: number;
  page: number;
  pageSize: number;
};

type Props = {};

export type PrefilledDataConfig<T> =
  | {
      prefillActive: false;
    }
  | {
      prefillActive: true;
      initialLoading: boolean;
      initialData: PaginationResult<T> | null;
    };

/**
 * Controller that wraps the paginated table behaviour for the cursor paginated list of elements.
 * Handles the internal pagination, filters and sorters.
 *
 * Meant to be extended while providing field and filters definitions, and a way to fetch the data.
 */
export abstract class PaginatedTableController<T extends BaseColumnModel, P extends Props, S extends PaginatedTableState> extends BaseController<P, S> {
  /**
   * List of data items that will be displayed in the table.
   */
  protected data: T[];

  /**
   * Internal holder of sorters state.
   */
  protected sorters: GridSortModel;
  /**
   * Internal holder of filters state.
   * @protected
   */
  protected filters: Combiner | null;

  protected sorterMapper = new SorterMapper();
  protected filterMapper: FilterMapper;
  protected queryFilterMapper = new QueryFilterMapper();
  protected queryParamManager = new QueryParamManager();

  // Hold a ref of the loaded DataGrid: this ref is set in a callback only once the component
  // has mounted and the initial loading phase is complete, and only wraps the inner DataGrid
  private ref: HTMLElement | null = null;

  // Initialize the default filter when the component mounts to prevent remounts on each update
  private readonly DEFAULT_FILTER: Combiner;

  constructor(props: P, context: unknown) {
    super(props, context);

    this.filterMapper = this.getFilterMapper();

    this.data = [];
    this.sorters = [];
    this.filters = null;
    this.DEFAULT_FILTER = getDefaultCombiner();
  }

  componentDidMount() {
    super.componentDidMount();

    const prefilledConfig = this.getPrefilledConfig();
    if (!prefilledConfig.prefillActive) {
      this.doInitialDataFetch();
    } else if (this.state.initialDataLoad && !prefilledConfig.initialLoading) {
      this.setupPrefilledData(prefilledConfig);
    }
  }

  componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>): void {
    super.componentDidUpdate(prevProps, prevState);

    const prefilledConfig = this.getPrefilledConfig();
    if (prefilledConfig.prefillActive && this.state.initialDataLoad && !prefilledConfig.initialLoading) {
      this.setupPrefilledData(prefilledConfig);
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.teardownListeners();
  }

  getBaseState(): PaginatedTableState {
    return {
      ...super.getBaseState(),
      loadingData: true,
      initialDataLoad: true,
      hadAnyData: false,
      pageInfo: DEFAULT_PAGE_INFO,
      totalCount: 0,
      page: DEFAULT_PAGE,
      pageSize: this.getEffectiveDefaultPageSize(),
    };
  }

  /**
   *
   * Fetch the initial data.
   *
   * If you need to do something prior to the initial data fetching, like loading presets, override this method, but
   * call `this.handleDataFetch()` after you have fetched all you need to fetch.
   *
   * This is extracted from `componentDidMount` since we don't want to override `componentDidMount` without calling
   * `super.componentDidMount()` in it. That way we risk that we don't call important behaviour from the
   * BaseController.componentDidMount, and using a version of `super.super.componentDidMount` is "hacky" in JS.
   * This way, we hook our behaviour through the `doInitialDataFetch`.
   */
  protected doInitialDataFetch() {
    this.filters = this.getInitialFilters();
    this.handleDataFetch();
  }

  /* Internal options */
  /**
   * Provides the FilterMapper instance which will be used for filter mapping.
   *
   * If there's a specific case for how the filters handle different values,
   * we might need to provide a specific extension of FilterMapper.
   * In that case, override this method and return the right instance of the extended class.
   */
  protected getFilterMapper(): FilterMapper {
    return new FilterMapper();
  }

  /**
   * Options for the pagination page size.
   *
   * Override to add custom options.
   */
  protected getPageSizeOptions(): number[] {
    return [5, 10, 25, 50];
  }

  /**
   * Defines the initial page size.
   * We don't want to automate the selection to the lowest page size option.
   * If we did that, we would block the options for the better UX. This way,
   * we give the ability to change as necessary.
   *
   * Override to choose a different value.
   */
  protected getDefaultPageSize() {
    return 10;
  }

  /**
   * Attach additional DataGrid props which were not designated here.
   *
   * These will be merged with internal configuration to ensure consistency.
   */
  protected getAdditionalDataGridProps(): Partial<DataGridProProps<T>> {
    return {};
  }

  /**
   * Defines the component loading states.
   *
   * If overriden, don't forget to include invocation of `super.isLoading`:
   *
   * protected isLoading(): boolean {
   *     return newLoadingCondition || super.isLoading();
   * }
   */
  protected isLoading(): boolean {
    return this.state.loadingData || this.state.initialDataLoad;
  }

  /**
   * Get definition for the prefilled content.
   * We cover two main scenarios:
   *
   * - when the prefill is active
   * - when the prefill is not active
   *
   * If it is not active, then we just use the standard flow.
   *
   * If it is active, we still have to cover the initial load behaviour.
   * In that case, we expect the external information about the initial load being done.
   * Once we get the initial load as finished, it will set up the prefilled state internally.
   */
  protected getPrefilledConfig(): PrefilledDataConfig<T> {
    return { prefillActive: false };
  }

  protected getInitialFilters(): Combiner | null {
    if (this.areStoredFiltersActive()) {
      return this.parseUrlFilters();
    }

    return null;
  }

  /**
   * Parses the url filters by the combined key.
   *
   * If something is wrong, as in, someone tampered with the filter data or the saved filter is not valid anymore,
   * the filter will be ignored.
   */
  protected parseUrlFilters(): Combiner | null {
    const rawFilters = this.queryParamManager.extractQueryValue(this.getUrlFilterKey());

    if (rawFilters === null) {
      return null;
    }

    return this.queryFilterMapper.parseQueryValue(rawFilters, this.getFilterConfigurations());
  }

  /**
   * Toggle flag to store the filter state into the url.
   */
  protected shouldStoreFiltersInUrl() {
    return false;
  }

  private areStoredFiltersActive() {
    return this.shouldStoreFiltersInUrl() && this.getFilterConfigurations().length > 0;
  }

  protected storeFiltersInUrl(replaceState = false) {
    let filters: string | FilterOperator = '';
    if (this.filters !== null) {
      filters = this.queryFilterMapper.mapCombiner(this.filters);
    }
    this.queryParamManager.updateQueryValue(this.getUrlFilterKey(), filters, replaceState);
  }

  /**
   * Define a key for the url filter storage.
   */
  protected getUrlFilterKey() {
    return 'filter';
  }

  /* Handlers */
  /**
   * Handler for the remote data fetching.
   * Wraps up the pagination and loading behaviour of the table.
   *
   * Since we use cursor pagination, we have to track the direction of our "movement".
   * If we move a "page up", we take `first` n items `after` a cursor. Otherwise, we are
   * going "page down", so we take `last` n items `before` a cursor.
   *
   * After the pagination definition, `fetchData` is called to retrieve the results.
   * Results are properly stored internally, and we display the newly fetched results.
   */
  protected async handleDataFetch(pageUp: boolean = true): Promise<void> {
    const { pageInfo, pageSize, page, totalCount } = this.state;
    try {
      this.setState({ loadingData: true });

      const isFirstPage = page === 0;
      const isLastPage = page === Math.ceil(totalCount / pageSize) - 1;

      const cursorInfo: CursorInfo = isFirstPage
        ? // Fetching the first page, `after` is unnecessary
          { first: pageSize, after: null }
        : isLastPage
        ? // Fetching the last page, `before` is unnecessary and `last` is the amount of rows
          // that fills the page (if % is zero, then it's a full page)
          { last: totalCount % pageSize || pageSize, before: null }
        : pageUp
        ? // Fetching an intermediate page, send `before`/`after` cursors and full page size
          { after: pageInfo.endCursor, first: pageSize }
        : { before: pageInfo.startCursor, last: pageSize };

      // Fetch the actual data
      const { data, pageInfo: newPageInfo, totalCount: newTotalCount, currentPage = page } = await this.fetchData(cursorInfo);

      // Set up the internal state
      this.data = data;
      this.setState((prev) => ({
        loadingData: false,
        initialDataLoad: false,
        hadAnyData: prev.hadAnyData || data?.length > 0,
        pageInfo: newPageInfo,
        totalCount: newTotalCount,
        page: currentPage,
      }));
    } catch (e: unknown) {
      this.setState({ loadingData: false });
      this.handleError(e);
    }
  }

  /**
   * Handler for the prefilled data setup.
   *
   * This method is only called to load the initial state if prefilled config is active
   * and updates only when the initial load is done on the external component.
   *
   * This result is used to set the initial state: the page will be 0 (the first page),
   * and the previous state can be ignored.
   */
  private setupPrefilledData(prefilledConfig: PrefilledDataConfig<T>) {
    // If someone called this by accident, we will skip it.
    if (!prefilledConfig.prefillActive || prefilledConfig.initialLoading) {
      return;
    }

    let { initialData } = prefilledConfig;
    if (initialData === null) {
      initialData = BLANK_PAGINATION_RESULT;
    }

    const { data, pageInfo, totalCount, currentPage = 0 } = initialData;

    this.data = data;
    this.setState({
      loadingData: false,
      initialDataLoad: false,
      hadAnyData: data.length > 0,
      pageInfo,
      totalCount,
      page: currentPage,
    });
  }

  /**
   * Handles any filter changes from the connected FilterPanel.
   *
   * If there's a filter change, we reset the pagination to the initial state and
   * fetch the data.
   */
  @bind
  private handleFilterChange(filters: Combiner | null) {
    this.filters = filters;
    this.setState({ pageInfo: DEFAULT_PAGE_INFO, page: DEFAULT_PAGE }, () => {
      if (this.areStoredFiltersActive()) {
        this.storeFiltersInUrl();
      }
      this.handleDataFetch();
    });
  }

  /**
   * Handles any sorters change from the connected DataGrid.
   *
   * If there's a sorters change, we reset the pagination to the initial state and
   * fetch the data.
   */
  private handleSortersChange(sorters: GridSortModel) {
    this.sorters = sorters;

    this.setState({ pageInfo: DEFAULT_PAGE_INFO, page: DEFAULT_PAGE }, () => {
      this.handleDataFetch();
    });
  }

  /**
   * Handle page or page size change.
   *
   * If page size change, we reset to the first page and fetch the data.
   */
  private handlePageChange(page: number, pageSize: number) {
    let pageUp = page >= this.state.page;
    const perPageChange = pageSize !== this.state.pageSize;

    const newState: Partial<PaginatedTableState> = {
      pageSize,
      page,
    };

    if (perPageChange) {
      // Reset the pagination state to the first page
      newState.pageInfo = DEFAULT_PAGE_INFO;
      newState.page = DEFAULT_PAGE;
      pageUp = true;
    }

    this.setState(newState as PaginatedTableState, () => this.handleDataFetch(pageUp));
  }

  /**
   * Call when we want to set up loaded blank state.
   * Depends on the conditions around the table.
   */
  protected setupBlankState(state?: Partial<S>) {
    this.setState({
      loadingData: false,
      initialDataLoad: false,
      hadAnyData: false,
      pageInfo: DEFAULT_PAGE_INFO,
      totalCount: 0,
      page: 0,
      pageSize: this.getEffectiveDefaultPageSize(),
      ...(state ?? {}),
    });
  }

  /**
   * Call when we want to reset the table state.
   * Depends on the conditions around the table.
   */
  protected resetState(callback?: () => void) {
    this.data = [];
    this.sorters = [];
    this.filters = null;

    this.setState(this.getBaseState(), callback);
  }

  /**
   * Call when we want to reload everything.
   */
  protected reloadTableContent() {
    this.setState({ pageInfo: DEFAULT_PAGE_INFO, page: DEFAULT_PAGE }, () => {
      this.handleDataFetch();
    });
  }

  /* Table row handlers */
  /**
   * Checks if items is clickable.
   *
   * Meant to be overriden when needed.
   */
  protected isItemClickable(_item: T): boolean {
    return false;
  }

  /**
   * Handler for table row click.
   *
   * Meant to be overriden when needed.
   */
  protected onItemClick(_item: T): void {
    // do nothing
  }

  /**
   * Get class for the current row. Can be used to attach additional behaviour for the row.
   *
   * Meant to be overriden when needed.
   */
  protected getRowItemClass(item: T): string {
    if (this.isItemClickable(item)) {
      return 'clickable';
    }

    return '';
  }

  /* External mappers, can be used for the final use in the services. */
  /**
   * Get mapped sorters that can be used in GQL queries.
   *
   * Maps the internal model to GQL definition for sorters.
   */
  protected getSorters(): GQLSorterSet[] {
    const nameMap = new Map<string, string>(
      this.getFieldConfigurations()
        .filter((field) => field.sortable && field.gqlKeys?.sorterField)
        .map((field) => [field.fieldKey, field.gqlKeys?.sorterField as string])
    );
    return this.sorterMapper.mapSorters(this.sorters, nameMap);
  }

  /**
   * Get mapped filters that can be used in GQL queries.
   *
   * Maps the internal model to GQL definition for filters.
   */
  protected getFilters(): FilterOperator | null {
    if (this.filters === null) {
      return null;
    }

    return this.filterMapper.mapToGQLFilters(this.filters, this.getFilterConfigurations());
  }

  /**
   * Defines the header configuration for the toolbar.
   *
   * If `title` is provided, the default header will be displayed. Otherwise, it will remain hidden.
   *
   * This configuration determines the behavior of the `renderLeftToolbarElements` method in terms of header display.
   */
  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return {};
  }

  /*
   * Rendering methods
   *
   * Set of internal rendering methods that give us the ability to render the table and surrounding elements.
   */

  /**
   * Renders the table toolbar.
   *
   * Toolbar consists of left and right section with the collapsed filters.
   * If there is a filter definition, right section will prepend the filter button automatically.
   *
   * Otherwise, you can add whatever you need for the toolbar section.
   */
  protected renderToolbar(filterConfiguration: FilterConfiguration[]) {
    const hasFilters = filterConfiguration.length > 0;
    const showFilters = !this.state.initialDataLoad && hasFilters;

    const leftToolbarElements = this.renderLeftToolbarElements();
    const rightToolbarElements = this.renderRightToolbarElements();

    const hasElementsToRender = showFilters || leftToolbarElements !== null || rightToolbarElements !== null;
    if (!hasElementsToRender) {
      return <></>;
    }

    return (
      <Stack
        data-role="paginated-table-toolbar"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{ pr: 2, pl: showFilters ? 0 : 2, py: showFilters ? 0 : 2 }}
      >
        {leftToolbarElements !== null && <Box sx={{ flexGrow: 0, flexShrink: 0 }}>{leftToolbarElements}</Box>}
        {hasFilters && (
          <Box sx={{ flexGrow: 1, flexShrink: 1, overflowX: 'auto', py: 1.5, pl: 1.5 }} data-role="toolbar-filter-wrapper">
            {showFilters && <FilterPanel filter={this.filters ?? this.DEFAULT_FILTER} setFilters={this.handleFilterChange} configs={filterConfiguration} />}
          </Box>
        )}
        {rightToolbarElements !== null && <Box sx={{ flexGrow: 0, flexShrink: 0 }}>{rightToolbarElements}</Box>}
      </Stack>
    );
  }

  /**
   * Renders the left section of the toolbar.
   *
   * If `title` is provided in the configuration, the default header will be displayed. Otherwise, the left section remains empty.
   *
   * This method can be overridden if custom behavior is desired.
   */
  protected renderLeftToolbarElements(): ReactElement | null {
    const defaultHeaderConfiguration = this.getDefaultHeaderConfigurations();
    if (!defaultHeaderConfiguration.title) return null;
    const { totalCount, initialDataLoad } = this.state;
    const { title, subtitle, hideRowCount } = defaultHeaderConfiguration;
    return <TableHeader title={title} subtitle={subtitle} rowCount={hideRowCount ? undefined : totalCount} loading={initialDataLoad} />;
  }

  /**
   * Renders the right section of the toolbar.
   * If filters are active, filter button will be prepended to this section.
   */
  protected renderRightToolbarElements(): ReactElement | null {
    return null;
  }

  /**
   * Renders the table to the page.
   *
   * `tableComponent` is passed in so that we can attach it to the layout as intended.
   * If necessary, you can add other element before and/or after the table component, depending
   * on your needs.
   *
   * This was meant for override, so feel free to use it as such.
   */
  protected renderTable(tableComponent: ReactElement) {
    return <>{tableComponent}</>;
  }

  /**
   * Render table skeleton.
   *
   * We are using fieldConfiguration to get the number of columns for the skeleton.
   */
  private renderSkeleton(props: DataGridProProps<T>) {
    const columns = this.getFieldConfigurations().map((value) => ({
      ...this.mapFieldConfigurationToDataGridColumn(value),
      renderHeader: () => <Skeleton variant="rectangular" width={50} height={20} />,
      description: undefined,
      renderCell: () => <Skeleton variant="rectangular" width={100} height={20} />,
    }));

    // Cast to `unknown` and back to the model type to mock the behaviour for the skeleton.
    const numberOfSkeletonItems = this.getEffectiveDefaultPageSize();
    const rows = Array(numberOfSkeletonItems)
      .fill('')
      .map((_, idx) => ({ id: idx, getId: () => idx } as unknown as T));

    // Override to avoid using model directly.
    const skeletonProps = {
      ...props,
      getRowId: undefined,
      onRowClick: () => {},
      getRowClassName: () => '',
      columns,
      rows,
    };
    if (props.getDetailPanelContent) {
      // Override this method only if provided, or the skeleton will display a placeholder
      // for the disabled expansion button
      skeletonProps.getDetailPanelContent = () => undefined;
    }

    return <MuiDataGrid data-role="datagrid-skeleton" key="skeleton" {...skeletonProps} />;
  }

  /**
   * Renders the base component flow.
   * Wraps the base behaviour around setting up the filters, sorters, pagination, etc.
   *
   * If you need to override this, it might be better to deal with it in a separate flow with a different controller.
   */
  render() {
    const { totalCount, page, pageSize, initialDataLoad } = this.state;

    // Fetch base definitions so that we can use them
    const fieldConfigurations = this.getFieldConfigurations();
    const filterConfig = this.getFilterConfigurations();
    const pageSizeOptions = this.getEffectivePageSizeOptions();
    const columns = fieldConfigurations.map((value) => this.mapFieldConfigurationToDataGridColumn(value));

    const isLoadingData = this.isLoading();
    const disablePagination = isLoadingData || totalCount <= Math.min(...pageSizeOptions);
    const pageSizeAriaLabel = t('a11y.tables.changePageSize');
    const paginationProps: Partial<TablePaginationProps> = {
      showFirstButton: true,
      showLastButton: true,
      slotProps: {
        select: { native: true, inputProps: { title: pageSizeAriaLabel, 'aria-label': pageSizeAriaLabel, disabled: disablePagination } },
        actions: disablePagination
          ? {
              previousButton: { disabled: disablePagination },
              nextButton: { disabled: disablePagination },
              firstButton: { disabled: disablePagination },
              lastButton: { disabled: disablePagination },
            }
          : {},
      },
    };

    const props: DataGridProProps<T> = deepmerge(
      this.getAdditionalDataGridProps() as DataGridProProps<T>,
      {
        // general options
        disableColumnMenu: true,
        disableColumnReorder: true,
        disableVirtualization: true,
        disableRowSelectionOnClick: true,
        rowSelection: false,
        disableMultipleRowSelection: true,
        autoHeight: true,
        sortingMode: 'server',
        slotProps: { pagination: paginationProps },
        // pagination elements
        pagination: true,
        rowCount: totalCount,
        paginationModel: {
          page: Math.max(0, page), // clip for the -1 index we use for the default state
          pageSize,
        },
        pageSizeOptions: pageSizeOptions,
        paginationMode: 'server',
        // handlers
        onPaginationModelChange: (model) => this.handlePageChange(model.page, model.pageSize),
        onSortModelChange: (model) => this.handleSortersChange(model),
        onRowClick: ({ row }: { row: T }) => this.onItemClick(row),
        // option setters
        getRowClassName: ({ row }) => this.getRowItemClass(row),
        // data and field definitions
        rows: this.data,
        columns: columns,
        // styling
        sx: dataGridStyleOverrides,
        experimentalFeatures: { ariaV7: true },
      } as DataGridProProps<T>
    );

    // Render the component and connect everything.
    return this.renderBase(
      this.renderTable(
        <Paper>
          <ComponentLoader loading={isLoadingData && !initialDataLoad}>
            {this.renderToolbar(filterConfig)}
            {initialDataLoad ? (
              this.renderSkeleton(props)
            ) : (
              <Box data-role={`datagrid-${isLoadingData ? 'loading' : 'loaded'}`} ref={(ref) => this.setupListeners(ref as HTMLElement | null)}>
                <MemoizedMuiDataGrid {...props} />
              </Box>
            )}
          </ComponentLoader>
        </Paper>
      )
    );
  }

  /* Abstract methods */
  /**
   * Fetch the data that will be rendered in the component.
   *
   * This will be used in a pagination flow for the cursor paginated table display.
   */
  protected abstract fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<T>>;

  /**
   * Define fields for the table.
   */
  protected abstract getFieldConfigurations(): FieldConfiguration<T>[];

  /**
   * Define filter elements.
   * Filters are separated from the field definition since we can have filters for fields
   * that are not displayed in the table. In that case, we don't want to add "hidden" fields
   * just so that we can add a filter configuration.
   */
  protected abstract getFilterConfigurations(): FilterConfiguration[];

  /* Internal mappers and extractors */
  /**
   * Extracts the DataGrid definition from the field definition.
   */
  private mapFieldConfigurationToDataGridColumn(configuration: FieldConfiguration<T>): GridColDef<T> {
    // Connect and map necessary fields from configuration to DataGrid column definition.
    // We are not short-circuiting any properties since we don't want to relay internal field definitions
    // from our configuration to the GridColDef. That way we can both manipulate our definition
    // separately from the DataGrid AND we can upgrade DataGrid without changing our internal definition.
    const { label, description, columnProps = {}, sortable = false, fieldKey, render, renderHeader } = configuration;
    return {
      flex: 1,
      minWidth: 100,
      ...columnProps,
      filterable: false,
      sortable,
      field: fieldKey,
      headerName: label,
      renderCell: ({ row, tabIndex, hasFocus }) => render(row, { tabIndex, hasFocus }),
      renderHeader: () => {
        if (renderHeader) {
          return renderHeader();
        }
        // Override the default MUI header to comply with a11y rules about allowed attributes
        return <HeaderCell label={label} description={description} />;
      },
    };
  }

  private setupListeners(ref: HTMLElement | null) {
    this.ref = ref;

    if (!this.ref) return;
    this.ref.addEventListener('focusin', this.focusListener);
  }

  private teardownListeners() {
    if (!this.ref) return;
    this.ref.removeEventListener('focusin', this.focusListener);
    this.ref = null;
  }

  /**
   * BUMPME(@mui/x-data-grid-pro)
   * Support table scrolling on keyboard navigation to keep the currently focused cell
   * in the viewport. This is necessary to enable proper table scroll when the table does
   * not fit the current view.
   *
   * This should be handled by MUI in a future update, see
   * - https://github.com/mui/mui-x/issues/8054
   * - https://github.com/mui/mui-x/pull/6838
   *
   * The listener uses the `focusin` event instead of `focus`, the reason being that `focus`
   * does not bubble: given that the `ref` is attached to the outer table and not to each cell,
   * we need to receive all focus events of the children and identify the source by the `target`
   * attribute. See https://developer.mozilla.org/en-US/docs/Web/API/Element/focusin_event
   *
   * @param event the `focusin` event
   */
  @bind
  private focusListener(event: FocusEvent) {
    const target = event.target as HTMLElement;

    // `focusin` is triggered after `focus`: the target cell has already received the focus
    if (target !== document.activeElement) return;

    // Elements inside the cell might have received focus (links, buttons...): disregard any event
    // coming from elements that are not cells or descendants
    if (!target.matches('.MuiDataGrid-cell[tabindex="0"], .MuiDataGrid-cell[tabindex="0"] *')) return;

    const { top, left, bottom, right } = target.getBoundingClientRect();
    const isInViewport =
      top >= 0 &&
      left >= 0 &&
      bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      right <= (window.innerWidth || document.documentElement.clientWidth);

    if (!isInViewport) {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /**
   * Internal method to ensure consistency of the provided page size options.
   * Raises a `console.warn` if there are duplicates.
   * @returns a valid array of page sizes
   */
  private getEffectivePageSizeOptions() {
    const options = this.getPageSizeOptions();
    const effective = Array.from(new Set(options)).sort((a, b) => a - b);
    if (effective.length !== options.length) {
      console.warn(`Found duplicate or misplaced page size options [${options.toString()}]: using [${effective.toString()}]`);
    }
    return effective;
  }

  /**
   * Internal method to ensure consistency of the provided default page size with the available size options.
   * Raises a `console.warn` if the requested size is not available.
   * @returns a valid default page size
   */
  private getEffectiveDefaultPageSize() {
    const options = this.getEffectivePageSizeOptions();
    const pageSize = this.getDefaultPageSize();

    if (!options.includes(pageSize)) {
      console.warn(`Default page size ${pageSize} is not among the available page size options: defaulting to ${options[0]}`);
      return options[0];
    }
    return pageSize;
  }
}
